import { get, post } from './client'
import type { CustomerServiceConfig } from './cs.api'

// ── Queue Segmentation ────────────────────────────────────────────────────────

export type SegmentFilterOperator = 'equals' | 'contains' | 'starts_with'

export type SegmentFilter = {
  field:    string
  operator: SegmentFilterOperator
  value:    string
}

export type WorkflowSegment = {
  id:                 string
  name:               string
  color?:             string
  filters:            SegmentFilter[]
  assignedTeamIds:    string[]
  assignedAgentNames: string[]
}

export type WorkflowSegmentationConfig = {
  enabled:         boolean
  assignmentLevel: 'team' | 'individual'
  segments:        WorkflowSegment[]
}

// ── Agent Capacity ────────────────────────────────────────────────────────────

export type AgentCapacityMode = 'global' | 'per_workflow'

export type AgentCapacityConfig = {
  mode:        AgentCapacityMode
  global?:     { maxActiveTasks: number }
  perWorkflow?: {
    confirmation?:         number
    sales?:                number
    shipping_confirmation?: number
    customer_service?:     number
  }
}

export const DEFAULT_AGENT_CAPACITY_CONFIG: AgentCapacityConfig = {
  mode:   'global',
  global: { maxActiveTasks: 25 },
}

// ── Sales Commission ──────────────────────────────────────────────────────────

export type CommissionValueBase     = 'upsell_only' | 'full_order'
export type CommissionFormulaType   = 'fixed' | 'flat_percentage' | 'tiered'
export type CommissionPayoutTrigger = 'confirmed' | 'delivered' | 'collected'

export type CommissionTier = {
  upTo:        number | null  // null = unlimited top tier
  percentage:  number
}

export type SalesCommissionConfig = {
  enabled:          boolean
  valueBase:        CommissionValueBase
  formula:          CommissionFormulaType
  fixedAmount?:     number
  flatPercentage?:  number
  tiers?:           CommissionTier[]
  payoutTrigger:    CommissionPayoutTrigger
  excludeCancelled: boolean
  excludeReturned:  boolean
  clawbackDays?:    number
}

export const DEFAULT_COMMISSION_CONFIG: SalesCommissionConfig = {
  enabled:          false,
  valueBase:        'upsell_only',
  formula:          'flat_percentage',
  flatPercentage:   2,
  payoutTrigger:    'delivered',
  excludeCancelled: true,
  excludeReturned:  true,
}

// ── Queue Scheduling Policy ───────────────────────────────────────────────────
// Configuration model only — NO execution engine, NO scheduling algorithms.
// Consumed by settings UIs to define how the queue distributes orders to agents.

export type QueueCategory = 'priority' | 'postponed' | 'new'

export type QueueSchedulingPolicyType =
  | 'oldest_first'
  | 'highest_priority_first'
  | 'sequential'
  | 'even_distribution'
  | 'weighted_distribution'

export type WeightedDistribution = {
  priority: number   // percentage, must sum to 100 with other weights
  postponed: number
  new: number
}

export type QueueSchedulingPolicy =
  | { type: 'oldest_first' }
  | { type: 'highest_priority_first' }
  | { type: 'sequential'; order: QueueCategory[] }          // draggable order
  | { type: 'even_distribution' }
  | { type: 'weighted_distribution'; weights: WeightedDistribution }

export type QueueSchedulingConfig = {
  batchSize:        number
  schedulingPolicy: QueueSchedulingPolicy
}

export const DEFAULT_QUEUE_SCHEDULING_CONFIG: QueueSchedulingConfig = {
  batchSize:        10,
  schedulingPolicy: { type: 'oldest_first' },
}

export type ShopifyLocation = {
  id:   string
  name: string
}

export type GoogleSheet = {
  id:   string
  name: string
}

export type WarehouseConfig = {
  id:                   string
  name:                 string
  shopifyLocationId?:   string
  inventorySource:      'shopify' | 'google_sheet'
  googleSheetId?:       string
  googleSheetTab?:      string
  hasPickingSheet:      boolean
  warehouseType:        'main' | 'supporting'
  supportsWarehouseId?: string
  receivesPurchases?:   boolean
}

export type ReleaseStrategy = {
  mode:                  'immediately' | 'after_x_hours' | 'daily_schedule'
  afterXHours?:          number
  dailyScheduleTime?:    string
  dailyLookbackDays?:    number
  priorityOrders:        'follow_schedule' | 'bypass_schedule'
  includeExchangeOrders: boolean
  exchangeOrdersRelease: 'follow_schedule' | 'bypass_schedule'
}

// Unified model — three independent knobs:
//   method  — who gets each order (always individually assigned; no pool claiming)
//   display — how much of their assigned work agents see at once
//   pull    — configured separately in the Pull Policy panel
export type WorkflowDistributionStrategy = {
  method:    'round_robin' | 'fair_split'
  batchSize?: number
  display?:  'batch' | 'show_all'
}

export type FifoExecutionStrategy = 'event_driven' | 'manual' | 'scheduled' | 'interval'

export type FifoOperationalTags = {
  readyToShip:   string
  pendingStock:  string
  exchangeReady: string
}

export type FifoConfig = {
  enabled:             boolean
  priorityTagName:     string
  releaseStrategy:     ReleaseStrategy
  executionStrategies: FifoExecutionStrategy[]   // one or more active triggers
  scheduledTimes?:     string[]
  intervalHours?:      number
  operationalTags:        FifoOperationalTags
  staleReservedDays?:     number   // days in 'reserved' before showing stale alert (default 7)
  autoReceiveTransfers?:  boolean  // Jessica only: auto-receive transfer orders + re-run FIFO
}

// ── Confirmation attempt intervals ────────────────────────────────────────────

export type AttemptIntervalUnit = 'hour' | 'day' | 'week' | 'month'

export type AttemptInterval = {
  value: number
  unit:  AttemptIntervalUnit
}

export type TeamWorkingHours = {
  enabled:              boolean
  startTime:            string  // "HH:MM" 24-hour
  endTime:              string  // "HH:MM" 24-hour
  enableBreak:          boolean
  breakDurationMinutes: number
}

export type EmployeeSchedule = {
  customSchedule:        boolean
  startTime?:            string
  endTime?:              string
  breakDurationMinutes?: number
}

export type ConfirmationTeam = {
  id:                 string
  name:               string
  agents:             string[]
  workingHours?:      TeamWorkingHours
  employeeSchedules?: Record<string, EmployeeSchedule>
}

export type ConfirmationConfig = {
  enabled:                       boolean
  mode:                          'manual' | 'automatic' | 'hybrid' | null
  queuePolicy?:                  QueueSchedulingConfig
  confirmedTagName?:             string
  // Separate tag applied when an exchange order is confirmed (item 7).
  exchangeConfirmedTagName?:     string
  escalationHours?:              number
  // Added 'customer_service': CS team can act as the confirmation team (item 4).
  teamSource:                    'dedicated' | 'sales' | 'shipping_confirmation' | 'customer_service' | null
  teams:                         ConfirmationTeam[]
  releaseStrategy:               ReleaseStrategy
  distributionStrategy:          WorkflowDistributionStrategy
  // Contact Attempts — structured UX (item 5). Replaces free-text postponement options.
  contactAttempts:               number
  attemptIntervals:              AttemptInterval[]
  allowSpecificDatePostponement: boolean
  // Legacy postponement fields — kept for API compat.
  // TODO: Remove after backend migrates to contactAttempts / attemptIntervals.
  maxPostponements:              number
  postponementDurationOptions:   string[]
  expiredOrderAction:            'ship' | 'hold' | 'cancel'
  expiredOrderTimeoutHours:      number
  segmentation?:                 WorkflowSegmentationConfig
}

export type SalesTeam = {
  id:                 string
  name:               string
  agents:             string[]
  workingHours?:      TeamWorkingHours
  employeeSchedules?: Record<string, EmployeeSchedule>
}

export type SalesAfterDeliveryInterval = '1_day' | '1_month' | '3_months' | '6_months'

export type NpsAssignmentLevel = 'all' | 'team' | 'individual'

export type SalesNpsConfig = {
  enabled:      boolean
  level:        NpsAssignmentLevel
  teamIds?:     string[]
  agentNames?:  string[]
  batchSize?:   number
}

export type SalesConfig = {
  enabled:                boolean
  sameTeamAsConfirmation: boolean | null
  queuePolicy?:           QueueSchedulingConfig
  // Added 'customer_service': CS team can act as the sales agent source (item 4).
  teamSource:             'dedicated' | 'confirmation' | 'shipping_confirmation' | 'customer_service' | null
  teams:                  SalesTeam[]
  releaseStrategy:        ReleaseStrategy
  distributionStrategy:   WorkflowDistributionStrategy
  contactTiming:          ('before_shipping' | 'after_delivery')[]
  afterDeliveryIntervals: SalesAfterDeliveryInterval[]
  // Structured replacement for afterDeliveryIntervals — supports any [value][unit] pair.
  afterDeliveryDurations?: AttemptInterval[]
  readyTagPrefix:         string
  // Commission formula — configured by manager, used by CommissionSimulator + reports.
  commissionConfig?:      SalesCommissionConfig
  segmentation?:          WorkflowSegmentationConfig
}

export type ShippingConfirmationTeam = {
  id:                 string
  name:               string
  agents:             string[]
  workingHours?:      TeamWorkingHours
  employeeSchedules?: Record<string, EmployeeSchedule>
}

export type ShippingConfirmationConfig = {
  enabled:              boolean
  queuePolicy?:         QueueSchedulingConfig
  teamSource:           'dedicated' | 'confirmation' | 'sales' | 'customer_service' | null
  teams:                ShippingConfirmationTeam[]
  releaseStrategy:      ReleaseStrategy
  distributionStrategy: WorkflowDistributionStrategy
  segmentation?:        WorkflowSegmentationConfig
  maxPostponements?:         number
  expiredOrderTimeoutHours?: number
  expiredOrderAction?:       'ship' | 'hold' | 'cancel'
  contactAttempts?:               number
  attemptIntervals?:              AttemptInterval[]
  allowSpecificDatePostponement?: boolean
}

export type DeliveryTrackingConfig = { enabled: boolean; agents: string[] }

export type NpsConfigTrigger = { enabled: boolean; afterAmount: number; afterUnit: 'hours' | 'days' | 'months' }
export type NpsConfigQuestion = {
  id:       string
  order:    number
  type:     'rating' | 'text' | 'yes_no' | 'single_choice'
  text:     string
  required: boolean
  scale?:   5 | 10
  options?: string[]
}
export type NpsConfig = {
  enabled:                       boolean
  teamSource:                    'sales_team' | 'cs_team' | 'dedicated'
  agents?:                       string[]
  batchSize:                     number
  contactAttempts:               number
  attemptIntervals:              AttemptInterval[]
  allowSpecificDatePostponement: boolean
  triggerDelivery?:              NpsConfigTrigger
  triggerCsResolved?:            NpsConfigTrigger
  surveyVersion:                 number
  questions:                     NpsConfigQuestion[]
}

export type ShipmentDefaults = {
  sendAllowOpen:     boolean
  exchangeAllowOpen: boolean
  returnAllowOpen:   boolean
}

export type AirwayBillContent = {
  includeSkuQuantities: boolean
  includeOrderNotes:    boolean
}

// GET response — bostaApiKey is masked; only presence is indicated
export type ShippingProvidersConfig = {
  bostaEnabled:              boolean
  isBostaApiKeySet:          boolean
  useCustomWarehouseAddress: boolean
  warehouseAddress?:   { address: string; city: string; phone: string }
  fraudThresholds?:    { inboundMinHours: number; outboundMinHours: number }
  shipmentDefaults?:   ShipmentDefaults
  airwayBillContent?:  AirwayBillContent
}

// POST body — bostaApiKey is optional: omit to keep existing key unchanged
export type ShippingProvidersConfigInput = {
  bostaEnabled:              boolean
  bostaApiKey?:              string
  useCustomWarehouseAddress: boolean
  warehouseAddress?:   { address: string; city: string; phone: string }
  fraudThresholds?:    { inboundMinHours: number; outboundMinHours: number }
  shipmentDefaults?:   ShipmentDefaults
  airwayBillContent?:  AirwayBillContent
}

export type GeneralConfig = {
  timezone:                     string
  currency:                     string
  language:                     'ar' | 'en'
  workingHours?:                { start: string; end: string }
  pickupWarningThresholdHours?: number
}

export type PurchaseRequestConfig = {
  enabled:                 boolean
  receivingMethod:         'shopify' | 'google_sheet'
  overridePermissionRoles: string[]
}

export type InventoryPreviewResult = {
  ok:       { sku: string; quantity: number; inventoryItemId: string }[]
  rejected: { sku: string; reason: string }[]
}

export type IntegrationsStatus = {
  shopify: { connected: boolean; shopDomain: string }
  google:  { connected: boolean }
}

export type ShopifyStaffMember = {
  id:             string
  name:           string
  email:          string
  isAccountOwner: boolean
}

export type RoleAssignment = {
  role:           string | null
  isAccountOwner: boolean
}

export type RoleAssignments = Record<string, RoleAssignment>

function qs(shop: string) {
  return `?shop=${encodeURIComponent(shop)}`
}

export const settingsApi = {
  getShopifyLocations: (shop: string): Promise<ShopifyLocation[]> =>
    get(`/settings/warehouses/shopify-locations${qs(shop)}`),

  getGoogleSheetsList: (shop: string): Promise<GoogleSheet[]> =>
    get(`/settings/warehouses/google-sheets${qs(shop)}`),

  getGoogleSheetTabs: (shop: string, sheetId: string): Promise<string[]> =>
    get(`/settings/warehouses/google-sheets/${encodeURIComponent(sheetId)}/tabs${qs(shop)}`),

  createWarehouseInventorySheet: (shop: string, warehouseName: string): Promise<{ sheetId: string; tabName: string; url: string }> =>
    post(`/settings/warehouses/create-inventory-sheet${qs(shop)}`, { warehouseName }),

  getWarehouses: (shop: string): Promise<WarehouseConfig[]> =>
    get(`/settings/warehouses${qs(shop)}`),

  saveWarehouses: (shop: string, warehouses: WarehouseConfig[]): Promise<{ ok: boolean }> =>
    post(`/settings/warehouses${qs(shop)}`, warehouses),

  getFifoConfig: (shop: string): Promise<FifoConfig> =>
    get(`/settings/fifo${qs(shop)}`),

  saveFifoConfig: (shop: string, config: FifoConfig): Promise<{ ok: boolean }> =>
    post(`/settings/fifo${qs(shop)}`, config),

  getConfirmationConfig: (shop: string): Promise<ConfirmationConfig> =>
    get(`/settings/confirmation${qs(shop)}`),

  saveConfirmationConfig: (shop: string, config: ConfirmationConfig): Promise<{ ok: boolean }> =>
    post(`/settings/confirmation${qs(shop)}`, config),

  getSalesConfig: (shop: string): Promise<SalesConfig> =>
    get(`/settings/sales${qs(shop)}`),

  saveSalesConfig: (shop: string, config: SalesConfig): Promise<{ ok: boolean }> =>
    post(`/settings/sales${qs(shop)}`, config),

  getShippingConfirmationConfig: (shop: string): Promise<ShippingConfirmationConfig> =>
    get(`/settings/shipping-confirmation${qs(shop)}`),

  saveShippingConfirmationConfig: (shop: string, config: ShippingConfirmationConfig): Promise<{ ok: boolean }> =>
    post(`/settings/shipping-confirmation${qs(shop)}`, config),

  getShippingProvidersConfig: (shop: string): Promise<ShippingProvidersConfig> =>
    get(`/settings/shipping-providers${qs(shop)}`),

  saveShippingProvidersConfig: (shop: string, input: ShippingProvidersConfigInput): Promise<{ ok: boolean }> =>
    post(`/settings/shipping-providers${qs(shop)}`, input),

  getBostaAccountInfo: (shop: string): Promise<{ account: { name: string; id: string; phone: string } | null }> =>
    get(`/settings/shipping-providers/bosta/account${qs(shop)}`),

  getGeneralConfig: (shop: string): Promise<GeneralConfig> =>
    get(`/settings/general${qs(shop)}`),

  saveGeneralConfig: (shop: string, config: GeneralConfig): Promise<{ ok: boolean }> =>
    post(`/settings/general${qs(shop)}`, config),

  getIntegrationsStatus: (shop: string): Promise<IntegrationsStatus> =>
    get(`/settings/integrations/status${qs(shop)}`),

  previewWarehouseInventory: (
    shop: string,
    warehouseId: string,
    rows: { sku: string; quantity: number }[],
  ): Promise<InventoryPreviewResult> =>
    post(`/settings/warehouses/${encodeURIComponent(warehouseId)}/inventory-preview${qs(shop)}`, { rows }),

  applyWarehouseInventory: (
    shop: string,
    warehouseId: string,
    rows: { sku: string; quantity: number }[],
  ): Promise<{ applied: number; failed: number }> =>
    post(`/settings/warehouses/${encodeURIComponent(warehouseId)}/inventory-apply${qs(shop)}`, { confirmed: true, rows }),

  getPurchasingConfig: (shop: string): Promise<PurchaseRequestConfig> =>
    get(`/settings/purchasing${qs(shop)}`),

  savePurchasingConfig: (shop: string, config: PurchaseRequestConfig): Promise<{ ok: boolean }> =>
    post(`/settings/purchasing${qs(shop)}`, config),

  getShopifyUsers: (shop: string): Promise<ShopifyStaffMember[]> =>
    get(`/settings/team/shopify-users${qs(shop)}`),

  getTeamRoleAssignments: (shop: string): Promise<RoleAssignments> =>
    get(`/settings/team/role-assignments${qs(shop)}`),

  saveTeamRoleAssignments: (shop: string, assignments: RoleAssignments): Promise<{ ok: boolean }> =>
    post(`/settings/team/role-assignments${qs(shop)}`, assignments),

  getAgentCapacityConfig: (shop: string): Promise<AgentCapacityConfig> =>
    get(`/settings/agent-capacity${qs(shop)}`),

  saveAgentCapacityConfig: (shop: string, config: AgentCapacityConfig): Promise<{ ok: boolean }> =>
    post(`/settings/agent-capacity${qs(shop)}`, config),

  getCsConfig: (shop: string): Promise<CustomerServiceConfig> =>
    get(`/settings/customer-service${qs(shop)}`),

  saveCsConfig: (shop: string, config: Partial<CustomerServiceConfig>): Promise<CustomerServiceConfig> =>
    post(`/settings/customer-service${qs(shop)}`, config),

  getDeliveryTrackingConfig: (shop: string): Promise<DeliveryTrackingConfig> =>
    get(`/settings/delivery-tracking${qs(shop)}`),

  saveDeliveryTrackingConfig: (shop: string, config: DeliveryTrackingConfig): Promise<DeliveryTrackingConfig> =>
    post(`/settings/delivery-tracking${qs(shop)}`, config),

  getNpsConfig: (shop: string): Promise<NpsConfig> =>
    get(`/settings/nps${qs(shop)}`),

  saveNpsConfig: (shop: string, config: NpsConfig): Promise<NpsConfig> =>
    post(`/settings/nps${qs(shop)}`, config),

  getUnreachableConfig: (shop: string, workflow: UnreachableWorkflow): Promise<UnreachableConfig> =>
    get(`/settings/unreachable/${workflow}${qs(shop)}`),

  setUnreachableConfig: (shop: string, workflow: UnreachableWorkflow, config: UnreachableConfig): Promise<{ ok: boolean }> =>
    post(`/settings/unreachable/${workflow}${qs(shop)}`, config),

  getOrderCreationConfig: (shop: string): Promise<OrderCreationConfig> =>
    get(`/settings/order-creation${qs(shop)}`),

  setOrderCreationConfig: (shop: string, config: Partial<OrderCreationConfig>): Promise<OrderCreationConfig> =>
    post(`/settings/order-creation${qs(shop)}`, config),

  getOwnerEmail: (shop: string): Promise<{ email: string | null }> =>
    get(`/settings/owner-email${qs(shop)}`),
}

// ── Order Creation Settings ───────────────────────────────────────────────────

export type OrderCreationMode    = 'draft' | 'live'
export type AgentOrderPermission = 'full' | 'create_only' | 'suggest_only'

export type OrderCreationConfig = {
  mode:            OrderCreationMode
  agentPermission: AgentOrderPermission
}

export const DEFAULT_ORDER_CREATION_CONFIG: OrderCreationConfig = {
  mode:            'draft',
  agentPermission: 'create_only',
}

// ── Unreachable Config ────────────────────────────────────────────────────────

export type UnreachableWorkflow = 'cf' | 'sc' | 'st' | 'cs' | 'dt'

export type UnreachableConfig = {
  days:            number
  attemptsPerDay:  number[]
  afterExhaustion: 'cancel' | 'hold' | 'next_workflow'
}

export const DEFAULT_UNREACHABLE_CONFIG: UnreachableConfig = {
  days:            3,
  attemptsPerDay:  [3, 2, 1],
  afterExhaustion: 'hold',
}
